import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

// Site-wide banner shown whenever the LLM API is failing.
//
// The 4 Aug 2026 outage: the Anthropic account ran out of credits, every LLM
// call 400'd, and the system silently degraded — Henry answered real customers
// with canned "we're on it" templates, entity extraction dropped to regex so
// QUOTES STOPPED GOING OUT, and vision died. It ran for over half an hour and
// nobody knew, because the only evidence was scattered lines in the container
// log. This is the visible signal: whoever has the dashboard open sees it.
//
// Backend truth comes from /api/v1/version -> llm_health (see
// app/services/llm_health.py). Not dismissible on purpose — it clears itself
// the moment calls succeed again, so if it is on screen it is still happening.

const POLL_MS = 60000;

// What the operator should actually DO, per failure reason.
const HINTS = {
  credits: 'Top up the Anthropic account (console.anthropic.com → Plans & Billing).',
  auth: 'The API key is invalid or was rotated — check ANTHROPIC_API_KEY.',
  model: 'The configured model name is wrong — check ANTHROPIC_MODEL / ANTHROPIC_COMPOSER_MODEL.',
  rate_limit: 'Rate limited — usually transient. Check for a traffic spike.',
  network: 'The container cannot reach the API — check network/DNS.',
};

export default function LlmHealthBanner() {
  const [health, setHealth] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const { data } = await apiClient.get('/api/v1/version');
        if (!cancelled) setHealth(data?.llm_health ?? null);
      } catch (e) {
        // A failed poll is NOT evidence of an LLM problem (could just be a
        // network blip or a deploy) — stay quiet rather than cry wolf.
        if (!cancelled) setHealth(null);
      }
    };

    check();
    const id = setInterval(check, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Only ever render on an explicit unhealthy answer.
  if (!health || health.healthy !== false) return null;

  const hint = HINTS[health.reason] || 'Check the backend logs for the API error.';

  return (
    <div className="bg-red-600 text-white px-4 py-3 sm:px-6" role="alert">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="text-sm">
          <p className="font-semibold">
            AI assistant degraded — the LLM API is failing
            {health.reason ? ` (${health.reason})` : ''}
          </p>
          <p className="mt-1">
            Henry cannot compose real replies, vehicle/area details are not being
            extracted, and <strong>quotes are not going out</strong>. Incoming
            messages are being queued for manual review instead of auto-answered.
          </p>
          <p className="mt-1 opacity-90">{hint}</p>
          {health.down_since && (
            <p className="mt-1 text-xs opacity-75">
              Failing since {new Date(health.down_since).toLocaleString()} ·{' '}
              {health.consecutive_failures} consecutive failures
              {health.where ? ` · first seen by ${health.where}` : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
